<template>
  <div :class="overlayClasses">
    <div
        class="v-dialog"
        :style="dialogStyles"
    >
      <div
          class="screen-main"
      >
        <header :class="{ 'is-loading': isLoading }">
          <VLink
              v-if="feedEntityPointer"
              class="header-close"
              @click="close"
              material="arrow_back_ios"
          />
          <div
              v-if="haveHeaderSlot"
              class="header-content"
          >
            <!-- @slot Слот сложного заголовка (title + бейджи, подзаголовки, иконки статуса).
                 Контейнер с padding'ом и flex-раскладкой обёрнут автоматически — пишешь
                 прямо <div class="title">...</div> + <VBadges>...</VBadges>, без своей обёртки. -->
            <slot name="header"/>
          </div>
          <template v-else-if="titleText">
            <div
                class="title"
                :title="titleText"
            >
              {{ titleText }}
            </div>

            <VBadges v-if="haveBadgesSlot || entity?.isDeleted">
              <VBadge v-if="entity?.isDeleted" class="warning">Удалён</VBadge>
              <!-- @slot Доменные бейджи справа от дефолтного «Удалён». -->
              <slot name="badges"/>
            </VBadges>
          </template>

          <div class="toolbar">

            <!-- @slot для кастомных кнопок аля "скачать историю" -->

            <template v-if="!isLoading">
              <slot name="toolbar"/>
            </template>

            <VButton
                v-if="fileEntities !== null && isLoading === false"
                :disabled="filesButtonDisabledTitle !== null"
                :title="filesButtonDisabledTitle ?? 'Файлы'"
                material="attach_file"
                @click="showFilesDialog"
            />

            <VButton
                v-if="showEditButton && !isLoading"
                :disabled="isEditButtonDisabled"
                :title="editButtonDisabledTitle"
                material="edit"
                @click="$emit('edit-button:clicked')"
            />

            <button
                v-if="isContextMenuVisible"
                ref="menuButton"
                type="button"
                @click="menuVisible = !menuVisible"
            >
              <VIcon material="menu"/>
            </button>

            <button
                v-if="feedEntityPointer === null || hideFeed === true"
                type="button"
                @click="close"
            >
              <VIcon material="close"/>
            </button>
          </div>

          <!-- on-action в меню не передаём: решение «перечитать или закрыть»
               принимает сам диалог в onContextMenuItemDone по результату пункта -->
          <VPopup
              v-if="isContextMenuVisible"
              :target="$refs.menuButton"
              :visible="menuVisible"
              :offset="0"
              align="right"
              @update:visible="menuVisible = $event"
              @click="menuVisible = false"
          >
            <VContextMenu
                v-if="contextMenu"
                :items="contextMenu"
                :row="entity"
                :show-select-option="false"
                :show-open-option="false"

                @context-menu-item:done="onContextMenuItemDone"
            />
          </VPopup>
        </header>

        <nav
            v-if="tabs && tabs.length > 0"
            class="tabs"
        >
          <button
              v-for="tab in tabs"
              :key="tab.id"
              type="button"
              :class="{ active: tab.id === currentActiveTab?.id }"
              @click="onTabClick(tab)"
          >
            <span class="title">{{ tab.name ?? tab.title }}</span>
            <span
                v-if="tab.badge"
                class="badge"
            >{{ tab.badge }}</span>
          </button>
        </nav>

        <VForm :ref="setFormRef">
          <div
              class="main"
              :class="{ 'is-loading': isLoading }"
          >
            <VLoader v-if="isLoading"/>

            <!-- @slot Слот контента диалога. Потребитель сам раскладывает <section> с данными и <hr> между ними. -->
            <slot v-else/>
          </div>

          <div
              v-if="allErrors.length > 0 && !isLoading"
              class="errors"
          >
            <ul>
              <li
                  v-for="(error, index) in visibleErrors"
                  :key="index"
                  :class="{ clickable: error.fieldRef !== undefined }"
                  @click="onErrorClick(error)"
              >
                {{ error.message }}
              </li>
              <li
                  v-if="hiddenErrorsCount > 0"
                  class="more"
                  @click="errorsExpanded = true"
              >
                и ещё {{ hiddenErrorsCount }}
              </li>
            </ul>
          </div>

          <footer v-if="!hideFooter && !isLoading">
            <template v-if="haveFooterSlot">
              <!-- @slot Слот кастомного футера. Если не передан — рендерится дефолтный футер с кнопками Сохранить/Отмена -->
              <slot name="footer"/>
            </template>
            <template v-else>
              <VButton
                  theme="primary"
                  :disabled="isSubmitDisabled"
                  :title="submitDisabledTitle"
                  @click="onSubmitButtonClicked"
              >
                Сохранить
              </VButton>

              <VButton @click="close">
                Отмена
              </VButton>
            </template>
          </footer>
        </VForm>
      </div>

      <aside
          v-if="feedEntityPointer && !hideFeed"
          class="screen-feed"
      >
        <VFeedSection  ref="feed" :parent-entity="feedEntityPointer"/>
      </aside>
    </div>
  </div>
</template>

<style lang="scss" src="./VDialogV3.scss"/>

<script lang="ts">

import DialogsService from '@/core/services/Dialogs/DialogsService';
import Constants from "@/core/misc/Constants";

import type ContextMenuItem from '@/lib/components/VContextMenu/ContextMenuItem';
import type EntityPointer from "@/domains/EntityPointer/EntityPointer";

import VPopup from '@/lib/components/VPopup/VPopup.vue';
import VIcon from '@/lib/components/VIcon/VIcon.vue';
import VLoader from '@/lib/components/VLoader/VLoader.vue';
import VFeedSection from "@/core/services/Feeds/components/VFeedSection.vue";

const DIALOG_POSITIONS = ['center', 'top', 'right', 'bottom', 'left'];

/**
 * Описание одного таба в шапке диалога.
 * Подпись таба задаётся через name ИЛИ title
 */
export type DialogTab = {
  /** Уникальный id таба. Используется в v-model:active-tab. */
  id: string;
  /** Текст таба. Синоним title, имеет приоритет над ним. */
  name?: string;
  /** Текст таба. Синоним name, используется если name не задан. */
  title?: string;
  /** Опциональный бейдж справа от заголовка (число или строка). 0/'' не рисуются. */
  badge?: number | string;
};

/** Описание одной клиентской ошибки валидации, показываемой в плашке над футером. */
export type DialogError = {
  /** Текст ошибки. */
  message: string;
  /**
   * Ссылка на поле, к которому относится ошибка. Принимает инстанс компонента
   * (то, что лежит в this.$refs.xxx) или сразу HTMLElement.
   * Если задано — клик по ошибке скроллит к этому полю и фокусирует его.
   *
   * Пример: <VSelect ref="manager" /> → fieldRef: this.$refs.manager
   */
  fieldRef?: unknown;
};

/**
 * Публичный API формы (VForm), который использует диалог.
 * Используется как тип для реактивного поля formRef.
 */
type FormRef = {
  errors: DialogError[];
  isValid: boolean;
  validate: () => Promise<void>;
  scrollToFirstError: () => void;
  scrollToControl: (ref: unknown) => void;
  getFirstErrorControl: () => { $el?: HTMLElement } | null;
};

/** Сколько ошибок показывать до сворачивания в «и ещё N». */
const ERRORS_VISIBLE_LIMIT = 3;

export default defineComponent({
  name: 'VDialogV3',

  components: {
    VPopup,
    VIcon,
    VLoader,
    VFeedSection,
  },

  emits: [
    /**
     * Пользователь закрыл диалог: крестик, оверлей, кнопка «Закрыть».
     * НЕ эмитится при программном hide() — закрытие после submit'а
     * не считается отменой. Два исхода карточки: submit или close.
     */
    'close',

    'edit-button:clicked',
    'submit-button:clicked',

    /**
     * Легаси-путь сабмита: эмитится после валидации, если не передан submitMethod.
     * Защиты от повторного сабмита и resolve() здесь нет — их делает потребитель сам.
     * Для новых карточек — проп submitMethod.
     */
    'submit',

    'update:active-tab',
    'contextMenuItem:done',
  ],

  props: {
    /**
     * Сущность для VContextMenu
     */
    entity: {
      type: Object,
      default: null,
    },

    /**
     * Колбэк, вызываемый после пункта контекстного меню, callback которого
     * вернул ровно true (состояние изменилось). Предназначен для перечитывания
     * сущности — например в карточках-реестрах (тендер: удалить ставку,
     * отправить заявку, отказаться от участия), где после действия карточку
     * нужно перезагрузить и оставить открытой.
     *
     * Если проп не передан, на true карточка закрывается через resolve():
     * хост под ней получит true из showAsPromise и обновится.
     *
     * ВНИМАНИЕ: не передавай, если карточка держит несохранённый черновик
     * (открытая на редактирование сущность) — перезагрузка затрёт правки
     * пользователя. В таких карточках проп не передаётся вовсе.
     *
     * TODO переосмыслить, пока использовать с большой осторожностью или не использовать вовсе
     */
    onContextMenuAction: {
      type: Function as PropType<() => void>,
      default: null,
    },

    /**
     * Доменное контекстное меню одним пропом.
     *
     * Принимает массив ContextMenuItem (обычно геттер сервиса домена,
     * например AssembliesService.contextMenu).
     *
     * У несохранённой сущности (entity.isExisting === false) меню не показывается —
     * карточке проверять это самой не нужно.
     */
    contextMenu: {
      type: Array as PropType<ContextMenuItem[] | null>,
      default: null,
    },

    /**
     * Метод сохранения карточки. Возвращает true, если сохранение прошло.
     *
     * Если передан, диалог сам ведёт сабмит: валидация формы, блокировка
     * кнопки «Сохранить» и защита от повторного нажатия на время сохранения,
     * а при true — resolve() карточки (хост получит true из showAsPromise).
     * Карточке остаётся только вернуть результат сервиса:
     *
     *   :submit-method="submit"
     *   async submit() { return await SomeService.upsert(this.localEntity); }
     *
     * Если не передан — легаси-путь через событие submit.
     */
    submitMethod: {
      type: Function as PropType<(() => Promise<boolean>) | null>,
      default: null,
    },

    /**
     * Указатель сущности для фида (VFeedSection). Если передан — справа от
     * основного экрана рендерится колонка с фидом. Если null — фида нет.
     * Видимость управляется только наличием указателя, отдельного флага нет.
     *
     * Проверку прав (например isAdmin) делает потребитель:
     * :feed-entity-pointer="$core.currentUser.isAdmin ? positionPointer : null"
     */
    feedEntityPointer: {
      type: Object as PropType<EntityPointer | null>,
      default: null,
    },

    /**
     * Сущности, в которые можно загружать файлы. Если передан — в тулбаре
     * рисуется скрепка, открывающая FilesDialog. Если null — скрепки нет.
     *
     * Проверку прав делает потребитель, как и для фида.
     */
    fileEntities: {
      type: Array as PropType<EntityPointer[] | null>,
      default: null,
    },

    /** Сущности, чьи файлы показываются в FilesDialog только на чтение. */
    fileEntitiesInherited: {
      type: Array as PropType<EntityPointer[]>,
      default: () => [],
    },

    /** Не больше одного файла на сущность — пробрасывается в FilesDialog. */
    maxOneFile: {
      type: Boolean,
      default: false,
    },

    /**
     * Скрыть колонку фида, не меняя ширину диалога. Место, которое занимал фид,
     * отдаётся основному экрану. Нужно вкладкам с широкими таблицами: диалог
     * остаётся той же ширины, поэтому при переключении вкладок он не прыгает.
     *
     * Указатель сущности при этом не сбрасывается — потребитель биндит флаг
     * на текущую вкладку: :hide-feed="activeTab?.id === 'supply-offers'"
     */
    hideFeed: {
      type: Boolean,
      default: false,
    },

    /**
     * Позиция диалога. По умолчанию по центру экрана.
     * Остальные позиции "прижимают" диалог к выбранной стороне экрана.
     * Возможные варианты: center, top, right, bottom, left
     */
    position: {
      type: String,
      default: 'center',
      validator: (value: string) => DIALOG_POSITIONS.includes(value),
    },

    /** Ширина диалога. Задаётся как свойство ширины CSS. */
    width: {
      type: String,
      default: null,
      validator: (value: string) => Constants.CSS_SIZE_REGEXP.test(value) !== false,
    },

    /**
     * Максимальная ширина диалога. Применяется, только если width не задан.
     * Задаётся как свойство max-width CSS. По умолчанию 1366px.
     */
    maxWidth: {
      type: String,
      default: '1366px',
      validator: (value: string) => Constants.CSS_SIZE_REGEXP.test(value) !== false,
    },

    /** Высота диалога. Задаётся как свойство высоты CSS. */
    height: {
      type: String,
      default: null,
      validator: (value: string) => Constants.CSS_SIZE_REGEXP.test(value) !== false,
    },

    /** Закрывать диалог при клике по оверлею (фону за диалогом). По умолчанию выключено. */
    closeOnOverlayClick: {
      type: Boolean,
      default: false,
    },

    /**
     * Убрать размытие страницы под диалогом. Затемнение остаётся.
     *
     * Оверлей по-прежнему перехватывает клики — диалог модальный,
     * closeOnOverlayClick работает как обычно.
     */
    hideOverlayBlur: {
      type: Boolean,
      default: false,
    },

    /**
     * Спрашивать подтверждение при попытке закрыть.
     * Работает для всех путей закрытия: крестик, клик по оверлею, кнопка Отмена.
     */
    confirmBeforeClose: {
      type: Boolean,
      default: false,
    },

    /** Текст подтверждения закрытия. Используется когда confirmBeforeClose === true */
    closeConfirmMessage: {
      type: String,
      default: 'Уверены?',
    },

    /**
     * Показывает лоадер вместо контента и скрывает футер.
     * Используется пока данные для диалога загружаются асинхронно.
     *
     * null (по умолчанию) — состояние не задано явно, диалог сам определит
     * загрузку по entity (нет сущности → лоадер). Передавай явный boolean,
     * только если у карточки нестандартная логика загрузки.
     */
    loading: {
      type: Boolean,
      default: null,
    },

    /**
     * Простой текстовый заголовок. Если не задан — берётся entity.toString().
     * Для сложных заголовков используй слот #header
     */
    title: {
      type: String,
      default: null,
    },

    /** Показать кнопку редактирования (карандаш) в тулбаре */
    showEditButton: {
      type: Boolean,
      default: false,
    },

    /**
     * Состояние кнопки редактирования.
     *  - null/false: кнопка активна.
     *  - true: задизейблена без тултипа.
     *  - строка: задизейблена, строка показывается как тултип с причиной.
     */
    editButtonDisabled: {
      type: [String, Boolean] as PropType<string | boolean | null>,
      default: null,
    },

    /**
     * Состояние кнопки сабмита.
     *  - null/false: кнопка активна.
     *  - true: задизейблена без тултипа.
     *  - строка: задизейблена, строка показывается как тултип с причиной.
     *
     * Блокировку на время сохранения при submitMethod диалог ставит сам —
     * передавать сюда isSaving не нужно.
     */
    submitDisabled: {
      type: [String, Boolean] as PropType<string | boolean | null>,
      default: null,
    },

    /** Скрыть футер целиком (для диалогов без действий) */
    hideFooter: {
      type: Boolean,
      default: false,
    },

    /**
     * Массив табов для шапки диалога. Если не передан или пуст — панель табов не рендерится.
     * Активный таб управляется через v-model:active-tab.
     * Потребитель сам решает что показывать в слоте по текущему activeTab (через v-if).
     */
    tabs: {
      type: Array as PropType<DialogTab[] | null>,
      default: null,
    },

    /**
     * Активный таб (v-model:active-tab). Если не задан или невалиден —
     * диалог сам выберет первый таб и эмитнет update:active-tab наверх,
     * чтобы потребитель сразу получил корректное значение в свой v-if.
     */
    activeTab: {
      type: Object as PropType<DialogTab | null>,
      default: null,
    },

    /**
     * Массив клиентских ошибок валидации. Если непустой — над футером
     * показывается красная плашка со списком сообщений.
     *
     * Дополнительно: если форма (VForm) невалидна, в плашку автоматически
     * добавляется синтетическая ошибка «В форме есть ошибки», которая снимется
     * сама, как только пользователь исправит все поля.
     */
    errors: {
      type: Array as PropType<DialogError[]>,
      default: () => [],
    },
    /**
     * Прогнать валидацию формы сразу после монтирования диалога.
     * Полезно когда диалог открывается с уже заполненной сущностью —
     * пользователь сразу видит, что не так с переданными значениями,
     * без необходимости сначала "потрогать" каждое поле.
     */
    validateOnMount: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      menuVisible: false,
      errorsExpanded: false,
      /**
       * Ссылка на инстанс VForm. Заполняется через функциональный ref (setFormRef)
       * вместо обычного ref="form" — потому что $refs в Vue 3 не реактивен,
       * а нам нужно чтобы computed allErrors реактивно подписался на form.errors.
       *
       * До маунта формы — null.
       */
      formRef: null as FormRef | null,

      /**
       * Идёт сабмит через submitMethod (от нажатия до ответа сервиса).
       * Блокирует кнопку «Сохранить» и повторные вызовы submit().
       */
      isSubmitting: false,
    };
  },

  computed: {
    /**
     * Итоговый заголовок. Если проп title задан явно — используем его.
     * Иначе падаем на entity.toString(); нет сущности → нет заголовка.
     */
    titleText(): string | null {
      if (this.title !== null) {
        return this.title;
      } else if (this.entity !== null) {
        return this.entity.toString() ?? this.entity.entityTypeName;
      } else {
        return null;
      }
    },

    /**
     * Итоговое состояние загрузки. Если проп loading задан явно (не null) —
     * используем его. Иначе падаем на entity: нет сущности → лоадер.
     */
    isLoading(): boolean {
      if (this.loading !== null) {
        return this.loading;
      } else {
        return this.$core.utils.isNullOrUndefined(this.entity) === true;
      }
    },

    /**
     * Классы оверлея. Модификатор позиции (center/top/right/bottom/left)
     * вешается именно на оверлей, а не на сам диалог — потому что позиционирование
     * реализовано через flex-выравнивание ребёнка (диалога) внутри родителя (оверлея).
     * Сам оверлей всегда накрывает весь экран (inset: 0), класс меняет лишь
     * align-items / justify-content, т.е. куда прилепить диалог внутри оверлея.
     *
     * no-blur снимает с оверлея размытие, см. проп hideOverlayBlur.
     */
    overlayClasses() {
      const result: Record<string, boolean> = {
        'v-dialog-overlay': true,
        'no-blur':          this.hideOverlayBlur === true,
      };
      result[this.position] = true;
      return result;
    },

    dialogStyles() {
      const result: { width?: string; maxWidth?: string; height?: string } = {};
      if (this.width !== null) {
        result.width = this.width;
      } else {
        result.maxWidth = this.maxWidth;
      }
      if (this.height) {
        result.height = this.height;
      }
      return result;
    },

    /**
     * Показывать ли кнопку и попап контекстного меню: меню передано, диалог
     * не грузится и сущность не новая. Сущность без признака isExisting
     * меню не скрывает.
     */
    isContextMenuVisible(): boolean {
      if (this.contextMenu === null) {
        return false;
      } else if (this.isLoading === true) {
        return false;
      } else if (this.$core.utils.isNullOrUndefined(this.entity) === false && this.entity.isExisting === false) {
        return false;
      } else {
        return true;
      }
    },

    /**
     * Причина недоступности скрепки. null — скрепка активна.
     * У несохранённой сущности файлов быть не может.
     */
    filesButtonDisabledTitle(): string | null {
      if (this.$core.utils.isNullOrUndefined(this.entity) === false && this.entity.isExisting === false) {
        return 'Файлы можно прикрепить после сохранения';
      } else {
        return null;
      }
    },

    haveHeaderSlot(): boolean {
      return this.$slots['header'] !== undefined;
    },

    haveFooterSlot(): boolean {
      return this.$slots['footer'] !== undefined;
    },

    haveBadgesSlot(): boolean {
      return this.$slots['badges'] !== undefined;
    },

    /**
     * Унифицированная логика для пропов editButtonDisabled / submitDisabled,
     * принимающих String | Boolean | null. Дают итоговые значения для :disabled и :title.
     */
    isEditButtonDisabled(): boolean {
      return this.editButtonDisabled !== null && this.editButtonDisabled !== false
    },

    editButtonDisabledTitle(): string | null {
      if (typeof this.editButtonDisabled === 'string') {
        return this.editButtonDisabled;
      } else {
        return null;
      }
    },

    isSubmitDisabled(): boolean {
      if (!!this.submitDisabled) {
        return true;
      }

      /** идёт сохранение через submitMethod — повторное нажатие блокируем */

      else if (this.isSubmitting === true) {
        return true;
      }

      /** если есть ошибки, то блокируем сабмит */

      else if (this.allErrors.length > 0) {
        return true;
      } else {
        return false;
      }
    },

    submitDisabledTitle(): string | null {
      if (typeof this.submitDisabled === 'string') {
        return this.submitDisabled;
      } else {
        return null;
      }
    },

    /**
     * Текущий активный таб. Если activeTab из пропа есть в списке tabs — он.
     * Иначе — первый таб из списка (если он есть). Иначе — null.
     * Сам по себе ничего не эмитит — синхронизация делается в watcher'ах.
     */
    currentActiveTab(): string | null {
      if (!this.tabs || this.tabs.length === 0) {
        return null;
      }
      const found = this.tabs.find(tab => tab.id === this.activeTab?.id);
      if (found) {
        return found;
      }
      return this.tabs[0];
    },

    /**
     * Полный список ошибок для плашки: ошибки формы (VForm.errors) + доменные
     * из пропа errors. Ошибки формы идут первыми — они результат явного действия
     * пользователя (сабмит/правка) и важнее доменных.
     *
     * Завязка на form.errors реактивная через formRef в data. До маунта формы
     * formRef === null, тогда показываем только доменные ошибки из пропа.
     */
    allErrors(): DialogError[] {
      const result: DialogError[] = [];

      if (this.formRef !== null) {
        result.push(...this.formRef.errors);
      }

      result.push(...this.errors);

      return result;
    },

    /** Ошибки, видимые сейчас. До клика «и ещё N» — первые ERRORS_VISIBLE_LIMIT. */
    visibleErrors(): DialogError[] {
      if (this.errorsExpanded) {
        return this.allErrors;
      } else {
        return this.allErrors.slice(0, ERRORS_VISIBLE_LIMIT);
      }
    },

    /** Сколько ошибок скрыто за «и ещё N». 0 — если все видны. */
    hiddenErrorsCount(): number {
      if (this.errorsExpanded) {
        return 0;
      } else {
        return Math.max(0, this.allErrors.length - ERRORS_VISIBLE_LIMIT);
      }
    },

    /** Имя родительского диалога (компонента, который содержит VDialogV3) */
    parentDialogName(): string {
      const name = this.$parent?.$options?.__name || this.$parent?.$options?.name;
      if (!name) {
        throw new Error('VDialogV3 должен быть внутри именованного компонента');
      }
      return name;
    },
  },

  watch: {
    /**
     * Если currentActiveTab отличается от activeTab пропа — значит проп
     * был невалидным/пустым, а внутри мы выбрали первый таб.
     * Эмитим наверх, чтобы потребитель сразу получил корректное значение.
     */
    currentActiveTab: {
      immediate: true,
      handler(newValue: DialogTab | null) {
        if (newValue !== null && newValue.id !== this.activeTab?.id) {
          this.$emit('update:active-tab', newValue);
        }
      },
    },

    /** При смене набора ошибок (включая синтетическую) сворачиваем «и ещё N». */
    allErrors() {
      this.errorsExpanded = false;
    },
  },

  mounted() {
    this.setupClickListener();

    // Регистрируем close() как канал закрытия по Escape — чтобы Escape шёл
    // тем же путём, что крестик и оверлей: эмит close + confirmBeforeClose.
    // На момент маунта наш диалог — последний открытый с этим именем,
    // findDialogId в сервисе найдёт именно его.
    DialogsService.setCloseRequest(this.parentDialogName, this.close);

    if (this.validateOnMount === true) {
      // this.$nextTick(() => this.validate());
      this.validate()
    }

  },

  beforeUnmount() {
    this.removeClickListener();
  },

  methods: {
    renewFeed() {
      const feed = this.$refs.feed;
      if (this.$core.utils.isObject(feed)) {
        feed.renewData();
      }
    },

    /**
     * Открывает FilesDialog со своими и унаследованными файлами карточки.
     */
    showFilesDialog(): void {
      this.$core.dialogs.FilesDialog.show({
        title:                 this.titleText ?? 'Файлы',
        fileEntities:          this.fileEntities,
        fileEntitiesInherited: this.fileEntitiesInherited,
        maxOneFile:            this.maxOneFile,
      });
    },

    /**
     * Пункт контекстного меню выполнен. Пробрасываем событие наверх и решаем
     * по результату callback то же, что грид в реестре:
     * - ровно true (состояние изменилось) и передан onContextMenuAction —
     *   перечитываем сущность, карточка остаётся открытой;
     * - ровно true без onContextMenuAction — закрываем карточку через resolve(),
     *   хост получит true из showAsPromise и обновится (например после удаления);
     * - любой другой результат — карточку не трогаем.
     */
    onContextMenuItemDone(item: ContextMenuItem, row: Record<string, unknown>, result: boolean | void): void {
      this.$emit('contextMenuItem:done', item, row, result);

      if (result === true) {
        if (this.onContextMenuAction !== null) {
          this.onContextMenuAction();
        } else {
          this.$core.dialogs[this.parentDialogName].resolve();
        }
      } else {
        // действие ничего не изменило или отменено — карточку не трогаем
      }
    },

    /**
     * Функциональный ref для VForm. Используется вместо обычного ref="form",
     * чтобы попасть в реактивное поле formRef (this.$refs не реактивен в Vue 3,
     * computed allErrors не пересчитывался при появлении формы).
     *
     * Vue зовёт этот колбэк с инстансом компонента при маунте, и с null
     * при размонтировании.
     */
    setFormRef(el: unknown) {
      this.formRef = el as FormRef | null;
    },

    /**
     * Сабмит формы дефолтного футера.
     * С submitMethod — весь цикл ведёт submit(). Без него — легаси-путь:
     * гейт валидации validateAndFocus, при валидной форме эмит submit,
     * иначе он сам переключит вкладку и подскроллит к первому невалидному полю.
     */
    async onSubmitButtonClicked() {
      this.$emit('submit-button:clicked');

      if (this.submitMethod !== null) {
        await this.submit();
      } else if (await this.validateAndFocus()) {
        this.$emit('submit');
      } else {
        // форма невалидна — validateAndFocus уже показал ошибку
      }
    },

    /**
     * Публичный метод — полный цикл сохранения через submitMethod:
     * валидация формы, сохранение, при успехе resolve() карточки.
     * Повторный вызов, пока идёт предыдущий, игнорируется.
     *
     * Нужен потребителям с кастомным футером (слот #footer): их кнопка
     * сохранения зовёт submit() через ref на диалоге вместо своей обвязки.
     *
     * Возвращает true, если сохранение прошло.
     */
    async submit(): Promise<boolean> {
      let isSubmitted = false;

      /**
       * Локальная константа, а не this.submitMethod: сужение типа после проверки
       * на null TS не переносит через await, а проп за время сабмита не меняется.
       */
      const submitMethod = this.submitMethod;

      if (submitMethod === null) {
        throw new Error('VDialogV3.submit: не передан проп submitMethod');
      } else if (this.isSubmitting === true) {
        // сохранение уже идёт — повторный сабмит игнорируем
      } else {
        // Ставим до валидации: она асинхронная, второе нажатие не должно проскочить
        this.isSubmitting = true;

        try {
          if (await this.validateAndFocus() === true) {
            isSubmitted = await submitMethod() === true;
          } else {
            // форма невалидна — validateAndFocus уже показал ошибку
          }
        } finally {
          this.isSubmitting = false;
        }

        if (isSubmitted === true) {
          this.$core.dialogs[this.parentDialogName].resolve();
        } else {
          // сохранение не прошло — ошибку показал сервис, карточка остаётся открытой
        }
      }

      return isSubmitted;
    },

    /**
     * Публичный метод — гейт перед сохранением. Прогоняет валидацию формы,
     * и если она не прошла — переключается на вкладку с первой ошибкой и
     * скроллит к невалидному полю. Плашка «В форме есть ошибки» появляется
     * и исчезает сама через реактивную завязку allErrors на form.isValid.
     *
     * Нужен потребителям с кастомным футером (слот #footer) на легаси-пути
     * без submitMethod: там своя кнопка сохранения, дефолтный onSubmitButtonClicked
     * не отрабатывает, и без этого вызова карточка уходит на бек с невалидной формой.
     *
     * Возвращает true если форма валидна.
     */
    async validateAndFocus(): Promise<boolean> {
      const isValid = await this.validate();

      if (isValid) {
        return true;
      }
      else {
        this.switchToFirstErrorTab();
        this.formRef?.scrollToFirstError();
        return false;
      }
    },

    /**
     * Публичный метод — прогоняет валидацию формы.
     * Используется когда родительскому компоненту нужно сразу при открытии
     * увидеть статус полей (например, диалог открывается с уже заполненной
     * сущностью, и пользователю нужно сразу видеть ошибки).
     *
     * Возвращает результат валидации (isValid после прогонки).
     */
    async validate(): Promise<boolean> {
      if (this.formRef === null) return true;

      await this.formRef.validate();
      return this.formRef.isValid;
    },

    setupClickListener() {
      if (this.closeOnOverlayClick === true) {
        window.addEventListener('click', this.onOverlayClick);
      }
    },

    removeClickListener() {
      window.removeEventListener('click', this.onOverlayClick);
    },

    /**
     * Закрытие диалога пользователем (крестик, оверлей, кнопка «Закрыть»).
     * Эмитит close и закрывает диалог. hideIfOpen, а не hide: если обработчик
     * close (свой или дефолтный в DialogsService.show) уже закрыл диалог,
     * повторное закрытие молча пропускается — без warning о несуществующем диалоге.
     */
    async close() {
      if (this.confirmBeforeClose) {
        if (await this.$core.confirm(this.closeConfirmMessage)) {
          // этот эмит занят тем, что чистить урл после закрытия диалога
          this.$emit('close');
          DialogsService.hideIfOpen(this.parentDialogName);
        }
      } else {
        // этот эмит занят тем, что чистить урл после закрытия диалога
        this.$emit('close');
        DialogsService.hideIfOpen(this.parentDialogName);
      }
    },

    onOverlayClick(event: MouseEvent) {
      if (event.target === this.$el) {
        this.close();
      }
    },

    onTabClick(tab: DialogTab) {
      if (tab.id !== this.currentActiveTab?.id) {
        this.$emit('update:active-tab', tab);
      }
    },

    /**
     * Клик по строке ошибки в плашке. Если у ошибки задан fieldRef —
     * просим форму скроллить к нему и сфокусировать. Сама форма умеет
     * работать как с инстансом компонента, так и с HTMLElement.
     */
    onErrorClick(error: DialogError) {
      if (error.fieldRef !== undefined) {
        if (this.formRef !== null) {
          this.switchToControlTab(error.fieldRef);

          this.formRef.scrollToControl(error.fieldRef);
        } else {
          // форма не смонтирована — пропускаем
        }
      }
    },

    /**
     * Если первое невалидное поле лежит на неактивной вкладке — переключаемся
     * на неё. Вкладку ищем по DOM: от $el контрола поднимаемся до ближайшего
     * предка с data-tab-id (его проставляет потребитель на обёртке вкладки).
     */
    switchToFirstErrorTab() {
      if (!this.tabs || this.tabs.length === 0) return;

      const control = this.formRef?.getFirstErrorControl();
      const el = control?.$el;
      if (!(el instanceof HTMLElement)) return;

      const tabId = el.closest('[data-tab-id]')?.getAttribute('data-tab-id');
      if (!tabId) return;

      const targetTab = this.tabs.find(tab => tab.id === tabId);
      if (targetTab && targetTab.id !== this.currentActiveTab?.id) {
        this.$emit('update:active-tab', targetTab);
      }
    },

    /**
     * Ищет вкладку, на которой лежит контрол, и переключается на неё.
     * Вкладку ищем по DOM: от $el контрола поднимаемся до ближайшего предка
     * с data-tab-id (его проставляет потребитель на обёртке вкладки).
     */
    switchToControlTab(fieldRef: unknown) {
      if (!this.tabs || this.tabs.length === 0) return;

      const el = (fieldRef as { $el?: HTMLElement })?.$el ?? fieldRef;
      if (!(el instanceof HTMLElement)) return;

      const tabId = el.closest('[data-tab-id]')?.getAttribute('data-tab-id');
      if (!tabId) return;

      const targetTab = this.tabs.find(tab => tab.id === tabId);
      if (targetTab && targetTab.id !== this.currentActiveTab?.id) {
        this.$emit('update:active-tab', targetTab);
      }
    },
  },
});
</script>
